import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Send, CheckCircle2, Eye, XCircle, 
  RefreshCw, MessageSquare, Smartphone, Mail, DollarSign,
  Users, Target, Activity, Clock, ArrowUpRight, ArrowDownRight,
  Calendar, Zap, BarChart2, PieChartIcon, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = {
  rcs: '#6366f1',
  sms: '#f59e0b',
  success: '#10b981',
  failed: '#ef4444',
  pending: '#6b7280',
  delivered: '#3b82f6',
  read: '#8b5cf6'
};

const AnalyticsDashboardV2 = () => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [timeSeries, setTimeSeries] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [quotaSummary, setQuotaSummary] = useState(null);
  const [billingUsage, setBillingUsage] = useState(null);
  
  // Filters
  const [period, setPeriod] = useState('30');
  const [granularity, setGranularity] = useState('daily');

  useEffect(() => {
    fetchAllData();
  }, [period, granularity]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboard(),
        fetchTimeSeries(),
        fetchCampaigns(),
        fetchQuotas(),
        fetchBillingUsage()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/v2/analytics/dashboard/quick?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Dashboard error:', error);
    }
  };

  const fetchTimeSeries = async () => {
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(period) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await axios.get(
        `${API_URL}/v2/analytics/messages/timeseries?start_date=${startDate}&end_date=${endDate}&granularity=${granularity}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTimeSeries(response.data);
    } catch (error) {
      console.error('TimeSeries error:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/v2/analytics/campaigns?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Campaigns error:', error);
    }
  };

  const fetchQuotas = async () => {
    try {
      const response = await axios.get(`${API_URL}/v2/quotas/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotaSummary(response.data);
    } catch (error) {
      console.error('Quotas error:', error);
    }
  };

  const fetchBillingUsage = async () => {
    try {
      const response = await axios.get(`${API_URL}/v2/billing/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingUsage(response.data);
    } catch (error) {
      console.error('Billing error:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Données rafraîchies');
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getTrendIcon = (value) => {
    if (value > 0) return <ArrowUpRight className="w-4 h-4 text-green-500" />;
    if (value < 0) return <ArrowDownRight className="w-4 h-4 text-red-500" />;
    return null;
  };

  // Prepare chart data
  const channelData = dashboard?.channel_breakdown?.map(ch => ({
    name: ch.channel.toUpperCase(),
    value: ch.messages_sent,
    color: ch.channel === 'rcs' ? COLORS.rcs : COLORS.sms
  })) || [];

  const statusData = dashboard?.message_metrics ? [
    { name: 'Livrés', value: dashboard.message_metrics.total_delivered, color: COLORS.delivered },
    { name: 'Lus', value: dashboard.message_metrics.total_read, color: COLORS.read },
    { name: 'Échoués', value: dashboard.message_metrics.total_failed, color: COLORS.failed }
  ] : [];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="analytics-dashboard-v2">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tableau de bord Analytics</h1>
            <p className="text-gray-500 text-sm mt-1">
              Vue d'ensemble des performances de vos campagnes RCS
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-36">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 jours</SelectItem>
                <SelectItem value="14">14 jours</SelectItem>
                <SelectItem value="30">30 jours</SelectItem>
                <SelectItem value="90">90 jours</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Messages */}
          <Card className="border-l-4 border-l-indigo-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Messages envoyés</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {formatNumber(dashboard?.total_messages)}
                  </p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-full">
                  <Send className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
              {dashboard?.trend_messages && (
                <div className="flex items-center mt-2 text-sm">
                  {getTrendIcon(dashboard.trend_messages)}
                  <span className={dashboard.trend_messages >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {Math.abs(dashboard.trend_messages).toFixed(1)}%
                  </span>
                  <span className="text-gray-500 ml-1">vs période précédente</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delivery Rate */}
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Taux de livraison</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {dashboard?.delivery_rate?.toFixed(1) || 0}%
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <Progress 
                value={dashboard?.delivery_rate || 0} 
                className="mt-3 h-2"
              />
            </CardContent>
          </Card>

          {/* Read Rate */}
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Taux de lecture</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {dashboard?.read_rate?.toFixed(1) || 0}%
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Eye className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <Progress 
                value={dashboard?.read_rate || 0} 
                className="mt-3 h-2"
              />
            </CardContent>
          </Card>

          {/* Total Cost */}
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Coût total</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {formatCurrency(dashboard?.total_cost)}
                  </p>
                </div>
                <div className="p-3 bg-amber-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              {billingUsage && (
                <p className="text-xs text-gray-500 mt-2">
                  Coût/msg: {formatCurrency(dashboard?.total_cost / (dashboard?.total_messages || 1))}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Time Series Chart */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Évolution des messages</CardTitle>
                <Select value={granularity} onValueChange={setGranularity}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Heure</SelectItem>
                    <SelectItem value="daily">Jour</SelectItem>
                    <SelectItem value="weekly">Semaine</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={timeSeries?.data_points || []}>
                    <defs>
                      <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorDelivered" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      dataKey="period" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => value?.split('-').slice(-1)[0] || value}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="sent" 
                      name="Envoyés"
                      stroke="#6366f1" 
                      fillOpacity={1}
                      fill="url(#colorSent)" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="delivered" 
                      name="Livrés"
                      stroke="#10b981" 
                      fillOpacity={1}
                      fill="url(#colorDelivered)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Channel Distribution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Répartition par canal</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72 flex flex-col items-center justify-center">
                {channelData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height="70%">
                      <PieChart>
                        <Pie
                          data={channelData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {channelData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex gap-6 mt-2">
                      {channelData.map((entry, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-sm text-gray-600">
                            {entry.name}: {formatNumber(entry.value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Aucune donnée disponible</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quotas & Campaigns Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quota Usage */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Utilisation des quotas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {quotaSummary?.quotas ? (
                Object.entries(quotaSummary.quotas).slice(0, 4).map(([key, quota]) => (
                  <div key={key} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 capitalize">
                        {key.replace(/_/g, ' ')}
                      </span>
                      <span className="font-medium">
                        {quota.current_usage} / {quota.limit || '∞'}
                      </span>
                    </div>
                    <Progress 
                      value={quota.usage_percentage || 0} 
                      className={`h-2 ${quota.usage_percentage > 80 ? 'bg-red-100' : ''}`}
                    />
                    {quota.alert_level && (
                      <div className="flex items-center gap-1 text-xs text-amber-600">
                        <AlertCircle className="w-3 h-3" />
                        {quota.alert_message}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">Aucun quota configuré</p>
              )}
            </CardContent>
          </Card>

          {/* Recent Campaigns */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-500" />
                Campagnes récentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {campaigns.length > 0 ? (
                <div className="space-y-3">
                  {campaigns.map((campaign, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {campaign.campaign_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {campaign.messages_sent} messages • {campaign.delivery_rate?.toFixed(1)}% livrés
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Badge 
                          variant={campaign.delivery_rate >= 90 ? 'default' : 'secondary'}
                          className={campaign.delivery_rate >= 90 ? 'bg-green-100 text-green-700' : ''}
                        >
                          {campaign.read_rate?.toFixed(0)}% lus
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <Target className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Aucune campagne récente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Message Status Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Détails des messages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <Send className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold text-blue-700">
                  {formatNumber(dashboard?.message_metrics?.total_sent)}
                </p>
                <p className="text-sm text-blue-600">Envoyés</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <CheckCircle2 className="w-8 h-8 mx-auto text-green-500 mb-2" />
                <p className="text-2xl font-bold text-green-700">
                  {formatNumber(dashboard?.message_metrics?.total_delivered)}
                </p>
                <p className="text-sm text-green-600">Livrés</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <Eye className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                <p className="text-2xl font-bold text-purple-700">
                  {formatNumber(dashboard?.message_metrics?.total_read)}
                </p>
                <p className="text-sm text-purple-600">Lus</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <XCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
                <p className="text-2xl font-bold text-red-700">
                  {formatNumber(dashboard?.message_metrics?.total_failed)}
                </p>
                <p className="text-sm text-red-600">Échoués</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer Info */}
        <div className="text-center text-xs text-gray-400">
          Dernière mise à jour: {dashboard?.generated_at ? new Date(dashboard.generated_at).toLocaleString('fr-FR') : '-'}
        </div>
      </div>
    </Layout>
  );
};

export default AnalyticsDashboardV2;
